<script setup lang="ts">
import {
  computed,
  onMounted,
  provide,
  ref,
  watch,
} from 'vue';
import Button from 'primevue/button';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import ArrowDownRightIcon from '@primeicons/vue/arrow-down-right';
import BriefcaseIcon from '@primeicons/vue/briefcase';
import CalendarIcon from '@primeicons/vue/calendar';
import EllipsisVIcon from '@primeicons/vue/ellipsis-v';
import GlobeIcon from '@primeicons/vue/globe';
import SitemapIcon from '@primeicons/vue/sitemap';
import {
  EventBusEvents,
  useAccounts,
  useActivities,
  useCrm,
  useEndpoint,
  useEventBus,
  useOpportunities,
  useStringFunctions,
} from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import { ApiResponseBase, GetContactDetailResponse, GetContactAssociationResponse } from '@apparatix/types/Contacts';
import { useContactStore } from '@apparatix/stores/useContactStore';
import AssociationListItem from './AssociationListItem.vue';
import AssociationListContainer from './AssociationListContainer.vue';
import AccountLabelTag from './AccountLabelTag.vue';
import ContactTypeChip from './ContactTypeChip.vue';
import ContactDealsDocsPanel from '@apparatix/leads/ContactDealsDocsPanel.vue';
import ContactDetailsTab from '@apparatix/leads/ContactDetailsTab.vue';
import ContactNotesTab from '@apparatix/leads/ContactNotesTab.vue';
import { Section } from '@apparatix/library/core';
import type { MenuItem } from 'primevue/menuitem';
import { useConfirm } from 'primevue/useconfirm';
import Menu from 'primevue/menu';
import ConfirmDialog from 'primevue/confirmdialog';
import type { AccountAssociated } from '@apparatix/types/Account';
import ActivitiesTab from './activities/ActivitiesTab.vue';
import AddActivityButton from './activities/AddActivityButton.vue';
import { ActivityAddedEvent, ActivityDeletedEvent, ActivityUpdatedEvent } from './activities/commonProps';
import { InjectEventNames } from '@apparatix/constants';
import { Relationship } from '@apparatix/schemas/Relationship';
import { useRoute, useRouter } from 'vue-router';
import { CrmDrawerDataReadyEvent, DetailTabName, FlyoutNavigationContext } from './types';
import { Activity } from '@apparatix/schemas/Activities';
import MiniCard from '@apparatix/library/MiniCard.vue';
import { Columns, Column as ColumnSection } from '@apparatix/library/core';
import ConfigGate from '@apparatix/library/ConfigGate.vue';
import DetailDrawerHeader from './DetailDrawerHeader.vue';
import { SharedStyles } from './SharedStyles';


type Props = {
  id: number;
  hideClose?: boolean;
  activeTab?: DetailTabName;
}

const props = defineProps<Props>();

const { updateAccount } = useAccounts();
const { getFullName } = useStringFunctions();
const { handleDetailTabUpdate } = useCrm();
const { emitEvent } = useEventBus();
const route = useRoute();
const router = useRouter();

const {
  activityTabSections,
  getFormattedMiniCardTitle,
  insertOrUpdateActivityInList,
  isLoading: isActivitiesLoading,
  lastTask,
  loadActivities,
  removeActivityFromList,
  upcomingTask,
} = useActivities();

const { addActivityComponent, getMobileMenuItems } = useCreateActivity();

const confirm = useConfirm();
const store = useContactStore();

const activeTab = ref<DetailTabName>(props.activeTab || 'overview');

const activityParentObject = computed<Relationship>(() => ({ id: props.id, name: getFullName(store.contactDetails), type: 'Contact' }));


const accountMenuRef = ref();
const activeAccount = ref<AccountAssociated>();
const accountMenuItems = ref<MenuItem[]>([
  {
    label: 'Copy contact address to account',
    icon: ArrowDownRightIcon,
    disabled: store.invoiceFieldsLocked || ! store.contactDetails.addresses[0],
    command: () => {
      copyAccountAddressToContact();
    },
  },
]);

const onActivityAddedOrUpdated = (activity: Activity, shouldReload: boolean) => {
  if (shouldReload) {
    loadActivities(props.id, 'accounts');
  } else {
    insertOrUpdateActivityInList(activity);
  }
};

provide<ActivityAddedEvent>(InjectEventNames.crm.activities.activityCreated, onActivityAddedOrUpdated);
provide<ActivityUpdatedEvent>(InjectEventNames.crm.activities.activityUpdated, onActivityAddedOrUpdated);
provide<ActivityDeletedEvent>(InjectEventNames.crm.activities.activityDeleted, removeActivityFromList);

const copyAccountAddressToContact = () => {
  if (activeAccount.value.fullAddress) {
    confirm.require({
      header: 'Notice',
      message: `This account already has an address: ${activeAccount.value.fullAddress}. Are you sure you want to overwrite?`,
      group: 'overwriteAccountAddressConfirm',
      accept: () => confirmedCopyAddress(),
    });
    return;
  }
  confirmedCopyAddress();
};

const confirmedCopyAddress = () => {
  const contactDataPayload = { id: activeAccount.value.c2Id, addresses: [store.contactDetails.addresses[0]]};

  delete contactDataPayload.addresses[0].id;
  if (activeAccount.value.addressId) {
    contactDataPayload.addresses[0].id = activeAccount.value.addressId;
  }

  updateAccount({ data: { accounts: [contactDataPayload]}});
};

const onAccountOptionsClick = (e: MouseEvent, account: AccountAssociated) => {
  activeAccount.value = account;
  accountMenuRef.value.toggle(e);
};

const iconButtonPt = { root: { class: 'w-5 h-5 p-1 bg-transparent hover:bg-[var(--neutral-background)]' }};

const {
  isLoading: isDetailLoading, makeRequest: makeDetailRequest, setEndpointUrl: detailRequestEndpoint,
} = useEndpoint<ApiResponseBase<GetContactDetailResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.contactDetails = response.data.contact;
    store.options = response.data.options;
    store.invoiceFieldsLocked = response.data.permissions.some((permission) => permission.tag === 'invoiceFieldsLocked');

    emitEvent<CrmDrawerDataReadyEvent>(EventBusEvents.CrmDetailLoaded, { title: response.data.contact.fullName, type: 'Contact' });
  },
  toast: { showOnSuccess: false },
});

const {
  isLoading: isAssociationsLoading, makeRequest: makeAssociationsRequest, setEndpointUrl: makeAssociationsEndpoint,
} = useEndpoint<ApiResponseBase<GetContactAssociationResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    response.data.accounts = response.data.accounts?.filter(account => account.c2Status !== 'Inactive'); // Filters out auto-generated accounts
    store.contactAssociations = response.data;
  },
  toast: { showOnSuccess: false },
});

watch(
  () => props.id,
  (newContactId) => {
    if (newContactId) {
      detailRequestEndpoint(`/crm/contacts/${props.id}`);
      makeAssociationsEndpoint(`/crm/contacts/${props.id}/associations`);

      makeDetailRequest();
      makeAssociationsRequest();
    }
  },
  { immediate: true },
);

// todo: This currently has to load all leads and opportunities since we need the swimlanes and options from them. It would be nice to be able to split this out to a smaller API call.
const helper = useOpportunities();
helper.loadInitialState();

const handleNavigateForward = (context: FlyoutNavigationContext) => {
  const { basePath } = route.meta;
  const path = `${basePath}/detail/${context.view.toLowerCase()}/${context.id}`;
  router.push({ path });
};

onMounted(() => loadActivities(activityParentObject.value.id, 'contacts'));
</script>
<template>
  <DetailDrawerHeader
    v-if="!isDetailLoading"
    type="Contact"
    :menu-items="getMobileMenuItems(['Note', 'Reminder', 'Task'], 'contacts', activityParentObject)"
    :title="store.contactDetails.fullName"
    :subtitle="store.contactDetails.companyName"
  >
    <template #buttons>
      <AddActivityButton view="contacts" :parent-object="activityParentObject" />
    </template>
  </DetailDrawerHeader>
  <Tabs
    v-if="!isDetailLoading && !isAssociationsLoading"
    class="grow"
    :value="activeTab"
    @update:value="(val: DetailTabName) => handleDetailTabUpdate(val, () => activeTab = val)"
  >
    <TabList>
      <Tab value="overview" :class="SharedStyles.flyouts.tabClasses">
        <GlobeIcon />
        Overview
      </Tab>
      <Tab value="relationships" :class="SharedStyles.flyouts.tabClasses">
        <SitemapIcon />
        Relationships
      </Tab>
      <ConfigGate enable-for="useCrmCampaigns">
        <Tab value="deals" :class="SharedStyles.flyouts.tabClasses">
          <BriefcaseIcon />
          Deals &amp; Docs
        </Tab>
      </ConfigGate>
      <Tab value="activity" :class="SharedStyles.flyouts.tabClasses">
        <CalendarIcon />
        Activity
      </Tab>
    </TabList>

    <TabPanels class="!bg-[var(--neutral-background)] grow">
      <div class="w-full grid grid-cols-3 grid-flow-row auto-rows-auto lg:gap-x-9 gap-y-4 p-2 lg:p-9">
        <div class="flex flex-col gap-4 col-span-3">
          <TabPanel value="overview">
            <div class="flex flex-col gap-6">
              <Columns class="lg:gap-x-6">
                <ColumnSection v-if="lastTask" class="lg:max-w-[50%]">
                  <MiniCard
                    :icon="CalendarIcon"
                    icon-color="var(--p-blue-500)"
                    subtitle="Last Activity"
                    :title="getFormattedMiniCardTitle(lastTask)"
                    :loading="isActivitiesLoading"
                    :date-time="lastTask.startDateTime"
                    :hide-phone-button="true"
                    :hide-email-button="true"
                    @on:title-click="activeTab = 'activity'"
                  />
                </ColumnSection>
                <ColumnSection v-if="upcomingTask" class="lg:max-w-[50%]">
                  <MiniCard
                    :icon="CalendarIcon"
                    icon-color="var(--p-blue-500)"
                    subtitle="Upcoming Activity"
                    :title="getFormattedMiniCardTitle(upcomingTask)"
                    :loading="isActivitiesLoading"
                    :date-time="upcomingTask.startDateTime"
                    :hide-phone-button="true"
                    :hide-email-button="true"
                    @on:title-click="activeTab = 'activity'"
                  />
                </ColumnSection>
              </Columns>
              <ContactDetailsTab v-if="!isDetailLoading" />
              <ContactNotesTab v-if="!isDetailLoading" />
            </div>
          </TabPanel>
          <TabPanel value="relationships">
            <div class="flex flex-col gap-6">
              <Section class="mb-3" :footer="false">
                <AssociationListContainer
                  title="Accounts"
                  :item-count="store.contactAssociations.accounts?.length ?? 0"
                  :hide-add-button="true"
                >
                  <AssociationListItem
                    v-for="account in store.contactAssociations.accounts"
                    :id="account.c2Id"
                    :key="account.c2Id"
                    tab-id="relationships"
                    association-type="account"
                    :title="account.c2Name"
                    :subtitle="account.c2Type"
                    :border="true"
                    @navigate-forward="handleNavigateForward"
                  >
                    <div class="flex gap-3 items-center justify-end">
                      <Button
                        icon-only
                        severity="secondary"
                        size="small"
                        aria-label="View account options"
                        text
                        rounded
                        :pt="iconButtonPt"
                        @click="e => onAccountOptionsClick(e, account)"
                      >
                        <EllipsisVIcon />
                      </Button>
                    </div>
                    <template #footer>
                      <div class="w-full flex items-center gap-2" :class="[(account.relationships?.length > 0 || account.accountLabel?.value) ? 'pb-2 md:pb-4': '']">
                        <ContactTypeChip v-for="label in account.relationships" :key="label" :label="{contactLabelId: 0, contactLabelDisplayName: label}" />
                        <AccountLabelTag :label="account.accountLabel" />
                      </div>
                    </template>
                  </AssociationListItem>
                </AssociationListContainer>
              </Section>
            </div>
          </TabPanel>
          <TabPanel value="deals">
            <div class="flex flex-col">
              <template v-if="!isDetailLoading">
                <ContactDealsDocsPanel
                  @navigate-forward="handleNavigateForward"
                />
              </template>
            </div>
          </TabPanel>
          <TabPanel value="activity">
            <ActivitiesTab
              v-if="store.contactAssociations"
              view="contacts"
              :parent-object="activityParentObject"
              :sections="activityTabSections"
              :is-loading="isActivitiesLoading"
            />
          </TabPanel>
        </div>
      </div>
    </TabPanels>
  </Tabs>
  <ConfirmDialog group="overwriteAccountAddressConfirm" />
  <Menu ref="accountMenuRef" :model="accountMenuItems" :popup="true" />
  <component :is="addActivityComponent" />
</template>
